import { useState } from 'react';
import { BOT_TRIGGERS, describeEntry, type BotEntry, type BotFlowSummary, type BotTriggerKind } from '../types';

/**
 * When a bot answers.
 *
 * Several bots can be live at once, so each one needs to say what brings it in:
 * a keyword, being closed, or everything nobody else took. The engine tries
 * them lowest priority first and the first whose condition matches replies —
 * which is why the order is shown as a number the person can change rather than
 * hidden behind the list's sort.
 *
 * Editing writes to every version of the bot. The condition belongs to the bot,
 * not to v3 of it, and a client who set a keyword on the live version and then
 * published a draft would otherwise watch it revert with no warning.
 */
export function EntryCondition({ flow, onSave, busy, compact = false }: {
  flow: BotFlowSummary;
  onSave: (entry: BotEntry) => void;
  busy?: boolean;
  /** Read-only: the sentence, no controls. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<BotTriggerKind>(flow.triggerKind ?? 'always');
  const [words, setWords] = useState(flow.triggerValue ?? '');
  const [priority, setPriority] = useState(String(flow.priority ?? 100));
  const [interrupt, setInterrupt] = useState(flow.canInterrupt ?? false);

  const sentence = describeEntry(flow);

  if (compact || !onSave) {
    return <span className="text-(--color-ink-soft)">Answers {sentence}</span>;
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setKind(flow.triggerKind ?? 'always'); setWords(flow.triggerValue ?? ''); setPriority(String(flow.priority ?? 100)); setInterrupt(flow.canInterrupt ?? false); setOpen(true); }}
        className="tap text-left text-small text-(--color-ink-soft) hover:text-(--color-ink)"
        title="Change when this bot answers"
      >
        Answers {sentence} <span className="text-(--color-muted)">· change</span>
      </button>
    );
  }

  const valid = kind !== 'keyword' || words.split(',').some((w) => w.trim().length > 0);
  const field = 'rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 py-1.5 text-small outline-none focus:border-(--color-brand)';

  return (
    <form
      className="flex flex-col gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) p-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || busy) return;
        onSave({
          triggerKind: kind,
          triggerValue: kind === 'keyword' ? words : null,
          priority: Number(priority) || 100,
          canInterrupt: kind === 'keyword' ? interrupt : false,
        });
        setOpen(false);
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as BotTriggerKind)} aria-label="When this bot answers" className={field}>
          {BOT_TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {kind === 'keyword' && (
          <input
            value={words} onChange={(e) => setWords(e.target.value)} autoFocus
            placeholder="support, help, agent" aria-label="Words that start this bot"
            className={`${field} min-w-48 flex-1`}
          />
        )}

        <label className="flex items-center gap-1.5 text-small text-(--color-muted)">
          Order
          <input
            value={priority} onChange={(e) => setPriority(e.target.value.replace(/\D/g, '').slice(0, 3))}
            inputMode="numeric" aria-label="Order" className={`${field} w-16 text-center`}
          />
        </label>
      </div>

      {kind === 'keyword' && (
        <label className="flex items-start gap-2 text-small text-(--color-ink-soft)">
          <input type="checkbox" checked={interrupt} onChange={(e) => setInterrupt(e.target.checked)} className="mt-0.5 size-4 accent-(--color-brand)" />
          <span>
            Let it cut in mid-conversation
            <span className="block text-micro text-(--color-muted)">
              Off by default: a customer part-way through another bot keeps going in that one, because
              interrupting throws away the answer they were typing.
            </span>
          </span>
        </label>
      )}

      <p className="text-micro text-(--color-muted)">
        {BOT_TRIGGERS.find((t) => t.value === kind)?.hint} Lower order is tried first.
      </p>

      <div className="flex gap-2">
        <button type="submit" disabled={!valid || busy} className="tap rounded-(--radius-field) bg-(--color-brand) px-3 py-1.5 text-small font-semibold text-(--color-brand-ink) disabled:opacity-40">
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small">
          Cancel
        </button>
      </div>
    </form>
  );
}
